import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { suppliersApi, formatCurrency } from '../utils/api';
import { toast } from './Toast';
import Icon from './Icon';
import FilterSheet from './FilterSheet';
import { useIsMobile } from '../utils/useIsMobile';

// Owner indicator (issue #167): tells admin / בעלים/מטה that some suppliers are
// hidden from their staff, and — where the page has a money headline — by how
// much the staff figure is lower. Never shown to staff, nor while previewing as
// them (the effective-role flag covers both).
//
// `amount` must come from the SAME basis and period as the page's headline
// (each page computes it; see the PR 3 notes in the spec). `count` comes from
// a server-computed report when it has one, else from /hq-only-summary.

// Fetched per mount, deliberately NOT cached across pages: marking or
// un-marking a supplier happens in this same tab (management page, create
// modal, merge), and a cached count would keep the notice wrong until a hard
// reload. It is one indexed COUNT.
// A failed fetch must not read as "nothing is hidden" without a word: say so.
const fetchCount = () => suppliersApi.hqOnlySummary()
  .then(r => r.data?.supplier_count || 0)
  .catch(err => {
    console.error('hq-only-summary failed', err);
    toast('לא הצלחנו לבדוק אם יש ספקים מוסתרים מהצוות — כדאי לרענן את הדף', 'error');
    return 0;
  });

// Outside an AuthProvider (isolated component tests) there is no owner, so
// every export here simply renders nothing.
const useAuthOrNone = () => useAuth() || {};

export function useHqOnlyCount(override) {
  const { canSeeHqOnly, restaurantId } = useAuthOrNone();
  const [count, setCount] = useState(override ?? 0);
  useEffect(() => {
    if (override != null) { setCount(override); return undefined; }
    if (!canSeeHqOnly || restaurantId == null) { setCount(0); return undefined; }
    let alive = true;
    fetchCount().then(n => { if (alive) setCount(n); });
    return () => { alive = false; };
  }, [canSeeHqOnly, restaurantId, override]);
  return canSeeHqOnly ? count : 0;
}

// Copy (owner + copywriter pass, 2026-10-03): the reader is the person who
// CAN see — so the chip says what the page holds ("כולל מידע רגיש"), never
// "something is hidden", which read as if it were hidden from them. How many
// suppliers staff miss is stated once, in the detail.
export const hqOnlyCountText = (count) => (count === 1
  ? 'ספק אחד מוסתר מהם'
  : `${count} ספקים מוסתרים מהם`);
export const HQ_ONLY_CHIP_LABEL = 'כולל מידע רגיש';
export const HQ_ONLY_POP_TITLE = 'מוצג לך מידע שמנהלים וצופים לא רואים';

// A quiet chip beside the page title (owner decision 2026-10-03: the full-width
// strip pushed the numbers below the fold on every page for a fact the owner
// already knows). The detail — the amount and "צפייה כמנהל" — opens on tap.
// `message` replaces the detail sentence and `chipLabel` the chip text (dishes).
// `pending`: the page is refetching for a new period/filter and still shows the
// previous rows — the amount would belong to the OLD period under the NEW date
// picker, so it is withheld (the count-only sentence stays true) until the
// headline itself is current. Pass the same condition that gates the headline.
// `money` formats the amount: two decimals by default, whole shekels on the
// payment screens (/suppliers, /suppliers/paid, /credits) whose figures it
// explains — the same formatter the page's own cards use.
export default function HqOnlyNotice({ amount = null, basis = '', count: countOverride, message, chipLabel, pending = false, money = formatCurrency }) {
  const { canSeeHqOnly, viewAsTargets = [], setViewAs } = useAuthOrNone();
  const count = useHqOnlyCount(countOverride);
  const [open, setOpen] = useState(false);
  // Phone (owner + designer, 2026-10-03): most users are on mobile, and any
  // line of its own pushed the numbers below the fold. So on a phone the chip
  // shrinks to a "🔒 רגיש" pill in the title row (zero added height) and the
  // detail opens in the app's bottom sheet — the native "more info" pattern.
  const isMobile = useIsMobile();
  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, isMobile]);
  if (!canSeeHqOnly || count <= 0) return null;

  const hasAmount = !pending && typeof amount === 'number' && Math.abs(amount) >= 0.005;
  // A hidden supplier whose credits exceed its purchases nets negative: staff
  // then see a HIGHER figure, and the sentence must say so.
  const direction = amount < 0 ? 'גבוהים' : 'נמוכים';
  const detail = message || (hasAmount
    ? `${hqOnlyCountText(count)}, ולכן ${money(Math.abs(amount))} ${basis} לא נכלל אצלם — הם רואים סכומים ${direction} יותר.`
    : `${hqOnlyCountText(count)} — הם לא רואים אותם ואת הנתונים שלהם.`);
  const fullLabel = chipLabel || HQ_ONLY_CHIP_LABEL;
  const canPreview = viewAsTargets.includes('manager') && setViewAs;
  const preview = () => { setOpen(false); setViewAs('manager'); };

  return (
    <span className="hq-only-chip-wrap">
      <button
        type="button"
        className="hq-only-chip"
        aria-expanded={open}
        aria-haspopup={isMobile ? 'dialog' : 'true'}
        aria-label={fullLabel}
        onClick={() => setOpen(o => !o)}
      >
        <Icon name="lock" size={13} />
        <span>{isMobile ? 'רגיש' : fullLabel}</span>
      </button>
      {isMobile && (
        <FilterSheet open={open} onClose={() => setOpen(false)} title={HQ_ONLY_POP_TITLE}>
          <p className="hq-only-sheet-text">{detail}</p>
          {canPreview && (
            <button type="button" className="hq-only-pop-action" onClick={preview}>צפייה כמנהל</button>
          )}
        </FilterSheet>
      )}
      {open && !isMobile && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          <div className="hq-only-pop" role="note">
            <div className="hq-only-pop-title">{HQ_ONLY_POP_TITLE}</div>
            <div className="hq-only-pop-text">{detail}</div>
            {canPreview && (
              <button type="button" className="hq-only-pop-action" onClick={preview}>
                צפייה כמנהל
              </button>
            )}
          </div>
        </>
      )}
    </span>
  );
}

// The page-header form of the lock: the same orange badge the management
// cards carry, beside a restricted supplier's own page title.
export function HqOnlyBadge() {
  const { canSeeHqOnly } = useAuthOrNone();
  if (!canSeeHqOnly) return null;
  return (
    <span className="badge hq-only-badge" style={{ fontSize: '0.78rem' }}>
      <Icon name="lock" size={12} />
      בעלים/מטה בלבד
    </span>
  );
}

// Small lock beside an HQ-only supplier's name, for admin / בעלים/מטה only.
export function HqOnlyLock({ hqOnly, size = 13 }) {
  const { canSeeHqOnly } = useAuthOrNone();
  if (!canSeeHqOnly || !hqOnly) return null;
  return (
    <span className="hq-only-lock" role="img" aria-label="בעלים/מטה בלבד" title="בעלים/מטה בלבד">
      <Icon name="lock" size={size} />
    </span>
  );
}
